import Component from "@base/Component.js";
import config from "@/config.js";
import t from "@/services/translationService.js";

const SCATTER_TILTS = [-9, 6, -13, 10, -6, 14, -11, 8, -4, 12, -8, 5];

// Items are scattered at random within a field sized to the
// container's actual width and item count, so it never needs to grow wider
// than the viewport — only taller. Each item's position is seeded from its
// id plus a per-mount random salt: stable for the lifetime of one page visit
// (so resize/drag-reorder/edit-toggle never reshuffle it), but different on
// the next page load rather than being permanently fixed.
const MAX_PAIR_OVERLAP = 0.5; // a single neighbor may cover at most 50% of an item's area
// Overlap fractions are additive across neighbors (a safe over-estimate of
// the true covered area, by subadditivity of area), so capping the *sum* at
// MAX_TOTAL_OVERLAP guarantees at least ~1-this fraction of every item stays
// visible even when several polaroids land near the same spot.
const MAX_TOTAL_OVERLAP = 0.3;
const PLACEMENT_ATTEMPTS = 300;
const MAX_GROWTH_STEPS = 60; // safety cap on how far the field may grow taller looking for a valid spot
const BOUNDARY_MARGIN = 50; // px kept clear between an item's edge and the field's edge

function hashSeed(str) {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

// mulberry32: tiny seeded PRNG so each item's position is stable across
// re-renders and independent of its position in the admin's display order.
function mulberry32(seed) {
  let a = seed;
  return function () {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t2 = Math.imul(a ^ (a >>> 15), 1 | a);
    t2 = (t2 + Math.imul(t2 ^ (t2 >>> 7), 61 | t2)) ^ t2;
    return ((t2 ^ (t2 >>> 14)) >>> 0) / 4294967296;
  };
}

// Fraction of one item's own area that a same-sized item at `b` would cover
// if placed at `a` — plain axis-aligned rectangle intersection.
function overlapFraction(a, b, w, h) {
  const overlapW = Math.max(0, Math.min(a.x + w / 2, b.x + w / 2) - Math.max(a.x - w / 2, b.x - w / 2));
  const overlapH = Math.max(0, Math.min(a.y + h / 2, b.y + h / 2) - Math.max(a.y - h / 2, b.y - h / 2));
  return (overlapW * overlapH) / (w * h);
}

function computeScatterLayout(items, fieldWidth, itemW, itemH, salt) {
  const halfW = itemW / 2;
  const halfH = itemH / 2;

  // Field height is sized off an assumed modest column count — not the
  // actual, much wider, container — so the pile always keeps several rows
  // of breathing room instead of collapsing into a single line once the
  // field is wide. Horizontal spread still uses the real container width.
  const targetCols = Math.max(1, Math.min(6, Math.round(Math.sqrt(items.length))));
  const rows = Math.max(1, Math.ceil(items.length / targetCols));
  let fieldHeight = Math.max(itemH * 1.3, rows * itemH * (1 - MAX_PAIR_OVERLAP) + itemH);

  // Clamp bounds keep every item's edge at least BOUNDARY_MARGIN inside the
  // field; if the field's too narrow for that (e.g. a single item on a
  // narrow phone), fall back to dead center rather than an inverted range.
  // Width is fixed to the real container — it must never grow sideways —
  // but height is free to grow, so it's a `let`, extended below as needed.
  const minX = halfW + BOUNDARY_MARGIN;
  const maxX = Math.max(minX, fieldWidth - halfW - BOUNDARY_MARGIN);
  const minY = halfH + BOUNDARY_MARGIN;

  const ordered = [...items].sort((a, b) => (a.gearItemId < b.gearItemId ? -1 : 1));
  const placed = []; // { x, y, coverage } — coverage = how much of THIS item later neighbors have covered so far
  const positions = {};

  for (const item of ordered) {
    const rand = mulberry32((hashSeed(item.gearItemId) ^ salt) >>> 0);
    let candidate = null;
    // A narrow (e.g. mobile) width leaves much less valid area than a wide
    // one for the same item count, so a fixed-height field can genuinely run
    // out of room. Rather than ever accepting a position that breaks the
    // overlap cap, grow the field taller — vertical space is free — and
    // keep trying.
    for (let growth = 0; growth < MAX_GROWTH_STEPS && !candidate; growth++) {
      const maxY = Math.max(minY, fieldHeight - halfH - BOUNDARY_MARGIN);
      for (let attempt = 0; attempt < PLACEMENT_ATTEMPTS; attempt++) {
        const test = { x: minX + rand() * Math.max(maxX - minX, 1), y: minY + rand() * Math.max(maxY - minY, 1) };
        const ok = placed.every((p) => {
          const frac = overlapFraction(test, p, itemW, itemH);
          return frac <= MAX_PAIR_OVERLAP && p.coverage + frac <= MAX_TOTAL_OVERLAP;
        });
        if (ok) {
          candidate = test;
          break;
        }
      }
      if (!candidate) fieldHeight += itemH * 0.5;
    }
    if (!candidate) candidate = { x: (minX + maxX) / 2, y: fieldHeight - halfH - BOUNDARY_MARGIN };
    for (const p of placed) {
      const frac = overlapFraction(candidate, p, itemW, itemH);
      if (frac > 0) p.coverage += frac;
    }
    positions[item.gearItemId] = candidate;
    placed.push({ x: candidate.x, y: candidate.y, coverage: 0 });
  }

  return { positions, fieldHeight };
}

/**
 * Gear pile: gear photos scattered like spilled Instax prints, title
 * "written" in marker font on each print's bottom border.
 *
 * View-only. Adding, editing and reordering gear happens in the Cockpit's Gear
 * Manager (#/cockpit/gear), which is why this no longer carries a view/edit
 * mode swap — the pile is always the scattered display it was designed to be.
 *
 * Clicking a print opens that item's detail page (#/gear/:id) via onOpen.
 *
 * props: { items: ShowcaseObject[], lang, admin, onOpen(id) }
 */
export default class GearPile extends Component {
  constructor(props) {
    super(props);
    this.state = { order: props.items.map((i) => i.gearItemId) };
    // A fresh random salt per mount, so the scattered layout looks different
    // on each page visit while staying put for the lifetime of this one
    // (resize/drag-reorder/edit-toggle must never reshuffle it mid-visit).
    this.scatterSalt = (Math.random() * 0xffffffff) >>> 0;
  }

  #byId(id) {
    return this.props.items.find((i) => i.gearItemId === id);
  }

  /**
   * Swap in a fresh item list - keeps existing items in their current order,
   * drops removed ones, appends newly created ones at the end.
   */
  updateItems(items) {
    const ids = new Set(items.map((i) => i.gearItemId));
    const kept = this.state.order.filter((id) => ids.has(id));
    const added = items.map((i) => i.gearItemId).filter((id) => !kept.includes(id));
    this.props.items = items;
    this.state.order = [...kept, ...added];
    this.setState({});
  }

  #orderedItems() {
    return this.state.order.map((id) => this.#byId(id)).filter(Boolean);
  }

  render() {
    const { lang, admin } = this.props;
    const items = this.#orderedItems();

    if (!items.length) {
      return `<section class="gear-pile-section"><div class="gear-pile gear-pile--empty"><p>${t.t("showcase.empty")}</p></div></section>`;
    }

    const tiles = items
      .map((item, i) => {
        const titleImage = item.titleImage();
        const img = titleImage
          ? `<img class="gear-pile__item-img" src="${config.imageBase}/showcase/${encodeURIComponent(item.gearItemId)}/${encodeURIComponent(titleImage)}?size=thumb" alt="${Component.escape(item.title(lang))}" loading="lazy" />`
          : `<div class="gear-pile__item-img gear-pile__item-img--placeholder"></div>`;
        const badge = admin && !item.isPublic() ? `<span class="gear-pile__item-badge">${item.isDraft() ? "draft" : "private"}</span>` : "";
        // x/y are computed post-mount (they need the container's actual
        // measured width/height) — see #layoutScattered.
        const style = `--tilt: ${SCATTER_TILTS[i % SCATTER_TILTS.length]}deg; --z: ${i + 1}`;
        return `
          <article class="gear-pile__item" data-id="${Component.escape(item.gearItemId)}" style="${style}">
            <div class="polaroid">
              <div class="gear-pile__item-media" role="button" tabindex="0">
                ${img}
                ${badge}
              </div>
              <p class="polaroid__caption">${Component.escape(item.title(lang))}</p>
            </div>
          </article>`;
      })
      .join("");

    return `<section class="gear-pile-section"><div class="gear-pile gear-pile--scattered">${tiles}</div></section>`;
  }

  onMounted() {
    this.#layoutScattered();
    this.listen(window, "resize", () => this.#layoutScattered());

    this.$$(".gear-pile__item").forEach((tile) => {
      const id = tile.getAttribute("data-id");
      const media = tile.querySelector(".gear-pile__item-media");
      const open = () => id && this.props.onOpen?.(id);
      this.listen(media, "click", open);
      this.listen(media, "keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          open();
        }
      });
    });
  }

  /**
   * Measures the container's real width and a rendered polaroid's real size,
   * then positions every tile via computeScatterLayout. Runs post-mount
   * (rather than in render()) because it needs actual DOM measurements to
   * stay responsive across breakpoints without ever growing sideways.
   */
  #layoutScattered() {
    const grid = this.$(".gear-pile");
    const tiles = this.$$(".gear-pile__item");
    if (!grid || !tiles.length) return;
    const items = this.#orderedItems();
    const sample = tiles[0].querySelector(".polaroid");
    const rect = sample.getBoundingClientRect();
    const itemW = rect.width || 220;
    const itemH = rect.height || 280;
    const fieldWidth = grid.clientWidth || itemW * 2;
    const { positions, fieldHeight } = computeScatterLayout(items, fieldWidth, itemW, itemH, this.scatterSalt);
    tiles.forEach((tile) => {
      const pos = positions[tile.getAttribute("data-id")];
      if (!pos) return;
      tile.style.left = `${Math.round(pos.x - itemW / 2)}px`;
      tile.style.top = `${Math.round(pos.y - itemH / 2)}px`;
    });
    grid.style.height = `${Math.round(fieldHeight)}px`;
    grid.classList.add("gear-pile--placed");
  }
}
